import {
  approvalSocket,
  enterTileRoom,
  leaveTileRoom,
} from '@mint-sdk/api-approval-flows/socket';
import { Manager, type Socket } from 'socket.io-client';

const SOCKET_PATH = '/api/approval-service/socket-io';

const port = globalThis.location.port;
const isSecure = port === '' || port === '443';
const protocol = isSecure ? 'wss' : 'ws';
const portSuffix = port === '443' ? '' : `:${port}`;
const WS_URL = `${protocol}://${globalThis.location.hostname}${portSuffix}`;

type TileRoomParams = {
  companyId: string;
  approvalItemId: string;
  approvalItemType: string;
};

let sharedSocket: Socket | null = null;
let connectionRefCount = 0;
let teardownTimer: ReturnType<typeof setTimeout> | null = null;

const roomRefCounts = new Map<string, number>();
const roomParams = new Map<string, TileRoomParams>();

const keyFor = (params: TileRoomParams): string =>
  `${params.approvalItemType}:${params.approvalItemId}:${params.companyId}`;

const rejoinAllRooms = (): void => {
  roomParams.forEach((params) => enterTileRoom(params));
};

const establishSocketConnection = (accessToken: string): Socket => {
  const manager = new Manager(WS_URL.concat(SOCKET_PATH), {
    transports: ['websocket'],
    path: SOCKET_PATH,
    query: {
      authorization: encodeURIComponent(accessToken),
    },
  });

  const rawSocket = manager.socket(SOCKET_PATH);

  approvalSocket.provideClient(rawSocket);

  return rawSocket;
};

export const acquireApprovalSocketConnection = (
  accessToken: string,
): Socket => {
  if (teardownTimer) {
    clearTimeout(teardownTimer);
    teardownTimer = null;
  }

  connectionRefCount += 1;

  if (!sharedSocket) {
    sharedSocket = establishSocketConnection(accessToken);
    sharedSocket.on('connect', rejoinAllRooms);
  }

  return sharedSocket;
};

export const releaseApprovalSocketConnection = (): void => {
  connectionRefCount = Math.max(0, connectionRefCount - 1);

  if (connectionRefCount > 0) {
    return;
  }

  teardownTimer = setTimeout(() => {
    teardownTimer = null;

    if (connectionRefCount > 0 || !sharedSocket) {
      return;
    }

    sharedSocket.off('connect', rejoinAllRooms);
    sharedSocket.close();
    sharedSocket = null;
    roomRefCounts.clear();
    roomParams.clear();
  }, 0);
};

export const joinEntityRoom = (params: TileRoomParams): void => {
  const key = keyFor(params);
  const count = roomRefCounts.get(key) ?? 0;

  roomRefCounts.set(key, count + 1);

  if (count === 0) {
    roomParams.set(key, params);

    if (sharedSocket?.connected) {
      enterTileRoom(params);
    }
  }
};

export const leaveEntityRoom = (params: TileRoomParams): void => {
  const key = keyFor(params);
  const count = roomRefCounts.get(key) ?? 0;

  if (count <= 1) {
    roomRefCounts.delete(key);
    roomParams.delete(key);

    if (sharedSocket?.connected) {
      leaveTileRoom(params);
    }
  } else {
    roomRefCounts.set(key, count - 1);
  }
};

export const __resetApprovalSocketConnectionForTests = (): void => {
  if (teardownTimer) {
    clearTimeout(teardownTimer);
    teardownTimer = null;
  }

  sharedSocket?.off('connect', rejoinAllRooms);
  sharedSocket?.close();
  sharedSocket = null;
  connectionRefCount = 0;
  roomRefCounts.clear();
  roomParams.clear();
};
